import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import { projectDataInitial } from 'app/edozvola/cases/case-main-project-documents/model/case-main-project-documents.model';
import {
  mainProjectDataValidationSchema,
  mainProjectDataWithNonMandatoryOibValidationSchema,
} from 'app/edozvola/cases/case-main-project-documents/model/case-main-project-documents.schema';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Button, Input } from 'edozvola-frontend-lib/dist/ui/components';
import CaseMainProjectDocumentsTable from 'app/edozvola/cases/case-main-project-documents/case-main-project-documents-table';
import {
  copyMainProjectDocumentsFromCaseQuery,
  fetchMainProjectDataQuery,
  saveMainProjectDataQuery,
} from 'app/edozvola/cases/case-main-project-documents/service/case-main-project-documents.queries';
import { AttachmentModalVariant } from 'app/edozvola/cases/project-documents/attachment-modal/attachment-modal';
import MainProjectDocumentAttachmentModal from 'app/edozvola/cases/case-main-project-documents/attachment-modal/attachment-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { projectDocumentsCaseUuidToCopyFromSchema } from 'app/edozvola/cases/project-documents/model/project-documents.schema';
import { CASE_TAB_ENUM } from 'app/config/constants';
import CaseMainProjectDocumentForm from 'app/edozvola/cases/case-main-project-documents/case-main-project-document-form';
import CaseMainProjectDocumentFormWithNonMandatoryOib from 'app/edozvola/cases/case-main-project-documents/case-main-project-document-form-with-non-mandatory-oib';
import CaseMainProjectDataFormWithNonMandatoryOib from 'app/edozvola/cases/case-main-project-documents/case-main-project-data-form-with-non-mandatory.oib';
import CaseMainProjectDataForm from 'app/edozvola/cases/case-main-project-documents/case-main-project-data-form';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { getCaseContext } from 'app/shared/util/context-util';

const CaseMainProjectDocuments = ({ loaderData = null }) => {
  const { id: uuid } = useParams<{ id: string }>();
  const { sidebarItemPair } = getCaseContext();
  const dispatch = useDispatch();

  const [idMainProjectDocument, setIdMainProjectDocument] = useState<string | null>(null);
  const [idMainProjectDocumentForAttachments, setIdMainProjectDocumentForAttachments] = useState<string | null>(null);
  const [mainProjectAttachmentVariant, setMainProjectAttachmentVariant] = useState<AttachmentModalVariant>();
  const [mainProjectReadOnly, setMainProjectReadOnly] = useState<boolean>();

  const { data: mainProjectData, isLoading: mainProjectDataLoading } = fetchMainProjectDataQuery(uuid);

  const mainProjectVariant = loaderData;

  const projectDataFormik = useFormik({
    initialValues: mainProjectData || projectDataInitial,
    validationSchema:
      mainProjectVariant === CASE_TAB_ENUM.MAIN_PROJECT_DOCUMENTS
        ? mainProjectDataValidationSchema
        : mainProjectDataWithNonMandatoryOibValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: values => saveMainProjectDataMutation(values),
  });

  const copyMainProjectDocumentsFormik = useFormik({
    initialValues: { caseUuidToCopyFrom: '' },
    validationSchema: projectDocumentsCaseUuidToCopyFromSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: values => copyMainProjectDocuments(values.caseUuidToCopyFrom),
  });

  const { mutate: copyMainProjectDocuments, isPending: isCopyMainProjectDocumentsPending } = copyMainProjectDocumentsFromCaseQuery(
    uuid,
    copyMainProjectDocumentsFormik.resetForm,
  );

  const { mutate: saveMainProjectDataMutation, isPending: isMainProjectDataSaving } = saveMainProjectDataQuery(
    uuid,
    projectDataFormik.resetForm,
  );

  const openNewMainProjectForm = () => {
    setIdMainProjectDocument('');
    setMainProjectReadOnly(false);
  };

  return (
    <>
      <FormikProvider value={projectDataFormik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />

        {mainProjectVariant === CASE_TAB_ENUM.MAIN_PROJECT_DOCUMENTS_V2 && (
          <CaseMainProjectDataFormWithNonMandatoryOib readOnly={sidebarItemPair.readOnly} projectDataFormik={projectDataFormik} />
        )}
        {mainProjectVariant === CASE_TAB_ENUM.MAIN_PROJECT_DOCUMENTS && (
          <CaseMainProjectDataForm readOnly={sidebarItemPair.readOnly} projectDataFormik={projectDataFormik} />
        )}
        <hr className={'mt-4'} />
        <div className={'d-flex justify-content-between'}>
          <div className={'form-header'}>Dijelovi projekta po strukovnim odrednicama</div>
          {!sidebarItemPair.readOnly && (
            <>
              <div className={'d-flex justify-content-end pb-4 gap-2'}>
                <div className={'case-copy-from-other-case'}>
                  <Input
                    placeholder={'Unesite šifru predmeta'}
                    maxLength={255}
                    addonAppend={
                      isCopyMainProjectDocumentsPending ? (
                        <FontAwesomeIcon icon="spinner" spin />
                      ) : (
                        <FontAwesomeIcon
                          aria-disabled={
                            (!!copyMainProjectDocumentsFormik.errors && !!copyMainProjectDocumentsFormik.errors.caseUuidToCopyFrom) ||
                            copyMainProjectDocumentsFormik.values.caseUuidToCopyFrom === ''
                          }
                          className={
                            (!!copyMainProjectDocumentsFormik.errors && !!copyMainProjectDocumentsFormik.errors.caseUuidToCopyFrom) ||
                            copyMainProjectDocumentsFormik.values.caseUuidToCopyFrom === ''
                              ? 'disabled'
                              : ''
                          }
                          icon="copy"
                        />
                      )
                    }
                    value={copyMainProjectDocumentsFormik.values.caseUuidToCopyFrom}
                    onChange={event => {
                      copyMainProjectDocumentsFormik.setFieldValue('caseUuidToCopyFrom', event.target.value);

                      if (copyMainProjectDocumentsFormik.submitCount > 0) {
                        copyMainProjectDocumentsFormik.setErrors({});
                      }
                    }}
                    addonAppendClick={() => {
                      if (!isCopyMainProjectDocumentsPending) {
                        copyMainProjectDocumentsFormik.handleSubmit();
                      }
                    }}
                    error={
                      copyMainProjectDocumentsFormik.errors && copyMainProjectDocumentsFormik.errors.caseUuidToCopyFrom
                        ? copyMainProjectDocumentsFormik.errors.caseUuidToCopyFrom
                        : ''
                    }
                  />
                </div>
                <div>
                  <Button label={'Dodaj dio projekta'} color={'info'} prependIcon={'plus'} size={'lg'} onClick={openNewMainProjectForm} />
                </div>
              </div>
            </>
          )}
        </div>
        <CaseMainProjectDocumentsTable
          setMainProjectDocument={setIdMainProjectDocument}
          setReadOnly={setMainProjectReadOnly}
          setMainProjectAttachmentVariant={setMainProjectAttachmentVariant}
          setMainProjectDocumentForAttachments={setIdMainProjectDocumentForAttachments}
        />
        {idMainProjectDocumentForAttachments !== null && (
          <MainProjectDocumentAttachmentModal
            idMainProjectDocument={idMainProjectDocumentForAttachments}
            type={mainProjectAttachmentVariant}
            setIdMainProjectDocument={setIdMainProjectDocumentForAttachments}
          />
        )}
        {idMainProjectDocument !== null && (
          <>
            {mainProjectVariant === CASE_TAB_ENUM.MAIN_PROJECT_DOCUMENTS && (
              <CaseMainProjectDocumentForm
                readOnly={mainProjectReadOnly}
                setIdMainProjectDocument={setIdMainProjectDocument}
                idMainProjectDocument={idMainProjectDocument}
              />
            )}
            {mainProjectVariant === CASE_TAB_ENUM.MAIN_PROJECT_DOCUMENTS_V2 && (
              <CaseMainProjectDocumentFormWithNonMandatoryOib
                readOnly={mainProjectReadOnly}
                setIdMainProjectDocument={setIdMainProjectDocument}
                idMainProjectDocument={idMainProjectDocument}
              />
            )}
          </>
        )}

        {!sidebarItemPair?.readOnly && (
          <div className={'case-save-button'}>
            <Button
              label={'Spremi'}
              size={'lg'}
              disabled={!projectDataFormik.dirty || !projectDataFormik.isValid}
              isLoading={isMainProjectDataSaving}
              color={'info'}
              onClick={() => projectDataFormik.handleSubmit()}
            />
          </div>
        )}
      </FormikProvider>
    </>
  );
};

export default CaseMainProjectDocuments;
